"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  Clock,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Building2,
  Phone,
  Mail,
  ShieldAlert,
  Search,
  RefreshCw,
  Send,
  PlusCircle,
  MinusCircle,
  ExternalLink,
  Crown,
  BellRing,
  Trash2,
  User,
  PhoneCall,
  MessageCircle,
  MessageSquare,
  X,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/Button";

interface TenantTrialData {
  id: number;
  username: string;
  fullName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  planTier: string;
  leadCount: number;
  createdAt: string;
  trialEndsAt?: string | null;
  industry?: string | null;
  status?: string;
  whatsappUsed?: number;
  whatsappLimit?: number;
  daysRemaining?: number | null;
  trialStatus?: "active" | "expiring_soon" | "expired" | "paid";
}

interface TenantTrialsTabProps {
  onSwitchWorkspace?: (tenantId: number) => void;
}

export default function TenantTrialsTab({ onSwitchWorkspace }: TenantTrialsTabProps) {
  const [tenants, setTenants] = useState<TenantTrialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "expiring_soon" | "expired" | "paid">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [toastMessage, setToastMessage] = useState<{ type: "success" | "error" | "info"; text: string } | null>(null);

  // Profile & Omnichannel Communication Modal
  const [selectedProfileTenant, setSelectedProfileTenant] = useState<TenantTrialData | null>(null);
  const [activeCommTab, setActiveCommTab] = useState<"overview" | "whatsapp" | "sms" | "trial">("overview");

  // Communication message states
  const [customSmsText, setCustomSmsText] = useState("");
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [notificationType, setNotificationType] = useState<"expiring_soon" | "expired" | "extension">("expiring_soon");
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);

  // Exact trial date state
  const [customExpiryDate, setCustomExpiryDate] = useState("");

  // Tenant Deletion Modal State
  const [tenantToDelete, setTenantToDelete] = useState<TenantTrialData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTrials = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/crm/admin/trials");
      const data = await res.json();
      if (data.success && data.tenants) {
        setTenants(data.tenants);
      }
    } catch (err) {
      console.error("Failed to load trial data:", err);
      showToast("error", "Failed to load tenant trial data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrials();
  }, []);

  const showToast = (type: "success" | "error" | "info", text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 5000);
  };

  // Adjust Trial Days (positive to extend, negative to decrease)
  const handleAdjustTrial = async (tenant: TenantTrialData, daysDelta: number) => {
    setActionLoadingId(tenant.id);
    try {
      const res = await fetch("/api/crm/admin/trials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "adjust",
          tenantId: tenant.id,
          days: daysDelta,
          sendNotification: daysDelta > 0,
        }),
      });
      const data = await res.json();

      if (data.success) {
        showToast(
          "success",
          daysDelta > 0
            ? `Extended trial for ${tenant.companyName || tenant.username} by ${daysDelta} days!`
            : `Reduced trial for ${tenant.companyName || tenant.username} by ${Math.abs(daysDelta)} days.`
        );
        await fetchTrials();
        // Update selectedProfileTenant if open
        if (selectedProfileTenant && selectedProfileTenant.id === tenant.id) {
          setSelectedProfileTenant((prev) =>
            prev
              ? {
                  ...prev,
                  trialEndsAt: data.newTrialEndsAt,
                  daysRemaining: data.daysRemaining,
                }
              : null
          );
        }
      } else {
        throw new Error(data.error || "Failed to adjust trial.");
      }
    } catch (err: any) {
      showToast("error", err.message || "Could not adjust trial.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Set Exact Expiry Date
  const handleSetExactDate = async () => {
    if (!selectedProfileTenant || !customExpiryDate) return;
    setActionLoadingId(selectedProfileTenant.id);
    try {
      const res = await fetch("/api/crm/admin/trials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "set_date",
          tenantId: selectedProfileTenant.id,
          endDate: customExpiryDate,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast("success", `Updated trial expiry to ${new Date(customExpiryDate).toLocaleDateString("en-IN")}.`);
        await fetchTrials();
        setSelectedProfileTenant((prev) =>
          prev
            ? {
                ...prev,
                trialEndsAt: data.newTrialEndsAt,
                daysRemaining: data.daysRemaining,
              }
            : null
        );
      } else {
        throw new Error(data.error || "Failed to set trial expiry date.");
      }
    } catch (err: any) {
      showToast("error", err.message || "Failed to set date.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Upgrade or Change Plan Handler
  const handleChangePlan = async (tenantId: number, newPlan: string) => {
    setActionLoadingId(tenantId);
    try {
      const res = await fetch("/api/crm/admin/trials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "change_plan",
          tenantId,
          planTier: newPlan,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast("success", `Updated plan to ${newPlan.toUpperCase()} successfully.`);
        await fetchTrials();
        if (selectedProfileTenant && selectedProfileTenant.id === tenantId) {
          setSelectedProfileTenant((prev) => (prev ? { ...prev, planTier: newPlan } : null));
        }
      } else {
        throw new Error(data.error || "Failed to update plan.");
      }
    } catch (err: any) {
      showToast("error", err.message || "Could not change plan.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Send Direct WhatsApp Template Notification
  const handleSendWhatsAppNotification = async () => {
    if (!selectedProfileTenant) return;
    setIsSendingWhatsApp(true);
    try {
      const res = await fetch("/api/crm/admin/trials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "notify",
          tenantId: selectedProfileTenant.id,
          notificationType,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast("success", `WhatsApp notification dispatched to ${selectedProfileTenant.phone}!`);
      } else {
        throw new Error(data.error || "Failed to dispatch alert.");
      }
    } catch (err: any) {
      showToast("error", err.message || "Failed to dispatch alert.");
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  // Send Direct SMS via Gateway
  const handleSendCustomSms = async () => {
    if (!selectedProfileTenant || !customSmsText.trim()) return;
    setIsSendingSms(true);
    try {
      const res = await fetch("/api/crm/admin/trials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "send_sms",
          tenantId: selectedProfileTenant.id,
          message: customSmsText.trim(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast("success", data.message || "SMS message dispatched successfully!");
        setCustomSmsText("");
      } else {
        throw new Error(data.error || "Failed to send SMS.");
      }
    } catch (err: any) {
      showToast("error", err.message || "Failed to send SMS.");
    } finally {
      setIsSendingSms(false);
    }
  };

  // Permanent Delete Tenant Handler
  const handleConfirmDeleteTenant = async () => {
    if (!tenantToDelete) return;
    setIsDeleting(true);
    try {
      const res = await fetch("/api/crm/admin/trials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "delete",
          tenantId: tenantToDelete.id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast("success", data.message || `Deleted tenant ${tenantToDelete.companyName || tenantToDelete.username}.`);
        setTenantToDelete(null);
        if (selectedProfileTenant && selectedProfileTenant.id === tenantToDelete.id) {
          setSelectedProfileTenant(null);
        }
        await fetchTrials();
      } else {
        throw new Error(data.error || "Failed to delete tenant.");
      }
    } catch (err: any) {
      showToast("error", err.message || "Failed to delete tenant.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Filtered tenants calculation
  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      if (filterStatus !== "all" && t.trialStatus !== filterStatus) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = (t.companyName || "").toLowerCase().includes(q);
        const matchUser = t.username.toLowerCase().includes(q);
        const matchPhone = (t.phone || "").includes(q);
        const matchEmail = (t.email || "").toLowerCase().includes(q);
        return matchName || matchUser || matchPhone || matchEmail;
      }
      return true;
    });
  }, [tenants, filterStatus, searchQuery]);

  // Overall metric counts
  const stats = useMemo(() => {
    return {
      total: tenants.length,
      active: tenants.filter((t) => t.trialStatus === "active").length,
      expiring: tenants.filter((t) => t.trialStatus === "expiring_soon").length,
      expired: tenants.filter((t) => t.trialStatus === "expired").length,
      paid: tenants.filter((t) => t.trialStatus === "paid").length,
    };
  }, [tenants]);

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Toast Alert */}
      {toastMessage && (
        <div
          className={`p-4 rounded-2xl flex items-center justify-between text-xs font-semibold shadow-md transition-all ${
            toastMessage.type === "success"
              ? "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
              : toastMessage.type === "error"
              ? "bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300"
              : "bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300"
          }`}
        >
          <div className="flex items-center gap-2.5">
            {toastMessage.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            )}
            <span>{toastMessage.text}</span>
          </div>
          <button
            onClick={() => setToastMessage(null)}
            className="text-text-muted hover:text-foreground text-xs underline cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header and Control Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-2xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
              <Clock className="w-4 h-4" />
            </span>
            <h3 className="text-base font-heading font-bold text-foreground">
              Tenant Trials &amp; Onboarding Lifecycle Manager
            </h3>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900/50">
              Super Admin Control
            </span>
          </div>
          <p className="text-xs text-text-muted mt-1 max-w-2xl">
            Monitor client trials, increase or decrease trial duration, view tenant profiles with direct WhatsApp, SMS, or Call links, and delete test tenants.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchTrials}
            disabled={loading}
            className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-foreground text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
        <div
          onClick={() => setFilterStatus("all")}
          className={`glass-card-premium rounded-2xl p-4 cursor-pointer transition-all border ${
            filterStatus === "all"
              ? "border-primary ring-2 ring-primary/20 bg-primary/5 dark:bg-primary/10 shadow-md"
              : "border-slate-200/80 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">All Tenants</span>
            <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
              <Building2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-heading font-black text-foreground">{stats.total}</div>
          <div className="text-[10px] text-text-muted mt-0.5">Total registered accounts</div>
        </div>

        <div
          onClick={() => setFilterStatus("active")}
          className={`glass-card-premium rounded-2xl p-4 cursor-pointer transition-all border ${
            filterStatus === "active"
              ? "border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 shadow-md"
              : "border-slate-200/80 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-emerald-700 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Active Trials
            </span>
            <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-heading font-black text-emerald-600 dark:text-emerald-400">{stats.active}</div>
          <div className="text-[10px] text-text-muted mt-0.5">&gt; 3 days remaining</div>
        </div>

        <div
          onClick={() => setFilterStatus("expiring_soon")}
          className={`glass-card-premium rounded-2xl p-4 cursor-pointer transition-all border ${
            filterStatus === "expiring_soon"
              ? "border-amber-500 ring-2 ring-amber-500/20 bg-amber-500/5 dark:bg-amber-500/10 shadow-md"
              : "border-slate-200/80 dark:border-slate-800 hover:border-amber-300 dark:hover:border-amber-700 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-amber-600 dark:text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Expiring Soon
            </span>
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-500">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-heading font-black text-amber-600 dark:text-amber-500">{stats.expiring}</div>
          <div className="text-[10px] text-text-muted mt-0.5">≤ 3 days left (Urgent)</div>
        </div>

        <div
          onClick={() => setFilterStatus("expired")}
          className={`glass-card-premium rounded-2xl p-4 cursor-pointer transition-all border ${
            filterStatus === "expired"
              ? "border-rose-500 ring-2 ring-rose-500/20 bg-rose-500/5 dark:bg-rose-500/10 shadow-md"
              : "border-slate-200/80 dark:border-slate-800 hover:border-rose-300 dark:hover:border-rose-700 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" />
              Expired Trials
            </span>
            <div className="p-2 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400">
              <ShieldAlert className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-heading font-black text-rose-600 dark:text-rose-400">{stats.expired}</div>
          <div className="text-[10px] text-text-muted mt-0.5">Need extension / upgrade</div>
        </div>

        <div
          onClick={() => setFilterStatus("paid")}
          className={`glass-card-premium rounded-2xl p-4 cursor-pointer transition-all border ${
            filterStatus === "paid"
              ? "border-purple-500 ring-2 ring-purple-500/20 bg-purple-500/5 dark:bg-purple-500/10 shadow-md"
              : "border-slate-200/80 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-700 shadow-sm"
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
              <Crown className="w-3.5 h-3.5" />
              Paid / Active
            </span>
            <div className="p-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400">
              <Crown className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-heading font-black text-purple-600 dark:text-purple-400">{stats.paid}</div>
          <div className="text-[10px] text-text-muted mt-0.5">Paying commercial tier</div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3.5 rounded-2xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-text-muted absolute left-3.5 top-3" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search company, username, phone..."
            className="w-full pl-9 pr-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-foreground placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary shadow-inner"
          />
        </div>

        <div className="flex items-center gap-1.5 self-end sm:self-auto overflow-x-auto">
          {(["all", "active", "expiring_soon", "expired", "paid"] as const).map((st) => (
            <button
              key={st}
              onClick={() => setFilterStatus(st)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all whitespace-nowrap cursor-pointer ${
                filterStatus === st
                  ? "bg-primary text-white shadow-sm font-bold"
                  : "bg-white/80 dark:bg-slate-800/80 text-text-muted hover:text-foreground border border-slate-200/60 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
              }`}
            >
              {st === "all" ? "All Accounts" : st.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Tenants Table */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 text-[11px] font-mono font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <th className="py-3 px-4">Workspace / Tenant</th>
                <th className="py-3 px-4">Contact &amp; Connect</th>
                <th className="py-3 px-4">Plan &amp; Trial Expiry</th>
                <th className="py-3 px-4 text-center">Pipeline Leads</th>
                <th className="py-3 px-4 text-center">WhatsApp Quota</th>
                <th className="py-3 px-4 text-right">Trial &amp; Profile Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-text-muted">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-primary" />
                    Loading tenant trial metrics...
                  </td>
                </tr>
              ) : filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-text-muted">
                    No tenants found matching this filter.
                  </td>
                </tr>
              ) : (
                filteredTenants.map((t) => {
                  const isBusy = actionLoadingId === t.id;
                  const isExpiring = t.trialStatus === "expiring_soon";
                  const isExpired = t.trialStatus === "expired";
                  const isActiveTrial = t.trialStatus === "active";
                  const isPaid = t.trialStatus === "paid";

                  const expiryFormatted = t.trialEndsAt
                    ? new Date(t.trialEndsAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "No Expiry";

                  const cleanPhone = (t.phone || "").replace(/[^0-9+]/g, "");

                  return (
                    <tr
                      key={t.id}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors group"
                    >
                      {/* Tenant & Industry */}
                      <td className="py-3.5 px-4">
                        <div
                          onClick={() => {
                            setSelectedProfileTenant(t);
                            setActiveCommTab("overview");
                          }}
                          className="flex items-start gap-3 cursor-pointer group-hover:opacity-90"
                          title="Click to view tenant profile and communication options"
                        >
                          <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0 border border-primary/20">
                            {t.companyName ? t.companyName.charAt(0).toUpperCase() : "T"}
                          </div>
                          <div>
                            <div className="font-heading font-bold text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
                              <span>{t.companyName || t.fullName || t.username}</span>
                              {isPaid && (
                                <span title="Paid Commercial Customer">
                                  <Crown className="w-3.5 h-3.5 text-amber-500" />
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-text-muted flex items-center gap-1 mt-0.5">
                              <span className="font-mono text-slate-500">@{t.username}</span>
                              {t.industry && (
                                <>
                                  <span>•</span>
                                  <span className="capitalize">{t.industry.replace("_", " ")}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Contact Info & One-Click Connect Actions */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1.5">
                          {t.phone ? (
                            <div className="flex items-center gap-2">
                              {/* Direct Call Link */}
                              <a
                                href={`tel:${cleanPhone}`}
                                className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-300 hover:text-primary transition-colors font-medium font-mono text-[11px]"
                                title="Click to Call Tenant directly"
                              >
                                <PhoneCall className="w-3 h-3 text-blue-500" />
                                <span>{t.phone}</span>
                              </a>

                              {/* WhatsApp Quick Chat Icon */}
                              <a
                                href={`https://wa.me/${cleanPhone.replace("+", "")}`}
                                target="_blank"
                                rel="noreferrer"
                                className="p-1 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 hover:bg-emerald-100 transition-colors"
                                title="Open WhatsApp Web chat"
                              >
                                <MessageCircle className="w-3 h-3 text-emerald-600" />
                              </a>

                              {/* Native SMS Link */}
                              <a
                                href={`sms:${cleanPhone}`}
                                className="p-1 rounded-md bg-sky-50 dark:bg-sky-950/40 text-sky-600 hover:bg-sky-100 transition-colors"
                                title="Send SMS via device"
                              >
                                <MessageSquare className="w-3 h-3 text-sky-600" />
                              </a>
                            </div>
                          ) : (
                            <span className="text-text-muted italic text-[11px]">No phone</span>
                          )}

                          {t.email && (
                            <div className="flex items-center gap-1.5 text-text-muted text-[11px]">
                              <Mail className="w-3 h-3 text-slate-400" />
                              <a
                                href={`mailto:${t.email}`}
                                className="hover:text-primary transition-colors truncate max-w-[150px]"
                                title="Email tenant"
                              >
                                {t.email}
                              </a>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Plan & Trial Status Badge */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          {isPaid ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                              <Crown className="w-3 h-3" />
                              {t.planTier.toUpperCase()} Active
                            </span>
                          ) : isExpired ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800 animate-pulse">
                              <ShieldAlert className="w-3 h-3" />
                              Expired ({Math.abs(t.daysRemaining || 0)}d ago)
                            </span>
                          ) : isExpiring ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                              <AlertTriangle className="w-3 h-3" />
                              Ending in {t.daysRemaining} days!
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                              <Clock className="w-3 h-3" />
                              Active ({t.daysRemaining}d left)
                            </span>
                          )}

                          <div className="text-[11px] text-text-muted flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span>Ends: {expiryFormatted}</span>
                          </div>
                        </div>
                      </td>

                      {/* Leads Count */}
                      <td className="py-3.5 px-4 text-center">
                        <span className="inline-block px-2.5 py-0.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-bold text-foreground text-xs">
                          {t.leadCount}
                        </span>
                        <div className="text-[10px] text-text-muted mt-0.5">Leads</div>
                      </td>

                      {/* WhatsApp Usage */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="text-xs font-semibold text-foreground">
                          {t.whatsappUsed || 0} / {t.whatsappLimit || 100}
                        </div>
                        <div className="w-20 bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full mx-auto mt-1.5 overflow-hidden">
                          <div
                            className="bg-emerald-500 h-full rounded-full transition-all"
                            style={{
                              width: `${Math.min(100, Math.round(((t.whatsappUsed || 0) / (t.whatsappLimit || 100)) * 100))}%`,
                            }}
                          />
                        </div>
                      </td>

                      {/* Action Controls: Profile, Adjust +/- Days, Plan, Workspace, Delete */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          {/* Profile & Omnichannel Action Trigger */}
                          <button
                            onClick={() => {
                              setSelectedProfileTenant(t);
                              setActiveCommTab("overview");
                            }}
                            className="h-7 px-2.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 text-[11px] font-bold flex items-center gap-1 transition-colors cursor-pointer"
                            title="Open Tenant Profile & Direct Communication (WhatsApp / SMS / Call)"
                          >
                            <User className="w-3 h-3" />
                            Profile &amp; Connect
                          </button>

                          {/* Decrease Trial (-7 Days) */}
                          <button
                            onClick={() => handleAdjustTrial(t, -7)}
                            disabled={isBusy}
                            className="h-7 px-2 rounded-lg bg-amber-50 dark:bg-amber-950/50 hover:bg-amber-100 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-[11px] font-bold flex items-center gap-0.5 transition-colors cursor-pointer disabled:opacity-50"
                            title="Decrease trial by 7 days"
                          >
                            <MinusCircle className="w-3 h-3" />
                            -7d
                          </button>

                          {/* Increase Trial (+7 Days) */}
                          <button
                            onClick={() => handleAdjustTrial(t, 7)}
                            disabled={isBusy}
                            className="h-7 px-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-[11px] font-bold flex items-center gap-0.5 transition-colors cursor-pointer disabled:opacity-50"
                            title="Increase trial by 7 days and send WhatsApp confirmation"
                          >
                            <PlusCircle className="w-3 h-3" />
                            +7d
                          </button>

                          {/* Plan Tier Dropdown */}
                          <select
                            value={t.planTier || "trial"}
                            onChange={(e) => handleChangePlan(t.id, e.target.value)}
                            disabled={isBusy}
                            className="h-7 text-[11px] font-semibold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-foreground rounded-lg px-2 focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-sm"
                          >
                            <option value="trial">Trial</option>
                            <option value="starter">Starter</option>
                            <option value="growth">Growth</option>
                            <option value="professional">Professional</option>
                            <option value="enterprise">Enterprise</option>
                          </select>

                          {/* Switch to Workspace */}
                          {onSwitchWorkspace && (
                            <button
                              onClick={() => onSwitchWorkspace(t.id)}
                              className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-text-muted hover:text-foreground flex items-center justify-center transition-colors cursor-pointer"
                              title="Open this tenant's CRM dashboard view"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {/* Delete Tenant Account Button */}
                          <button
                            onClick={() => setTenantToDelete(t)}
                            disabled={isBusy}
                            className="w-7 h-7 rounded-lg bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 flex items-center justify-center transition-colors cursor-pointer disabled:opacity-50"
                            title="Delete this test tenant account and its data"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant Profile & Omnichannel Communication Modal */}
      {selectedProfileTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-2xl glass-card-premium rounded-2xl p-6 shadow-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-foreground space-y-5 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary font-bold flex items-center justify-center text-lg border border-primary/20">
                  {selectedProfileTenant.companyName
                    ? selectedProfileTenant.companyName.charAt(0).toUpperCase()
                    : "T"}
                </div>
                <div>
                  <h3 className="font-heading font-black text-foreground text-lg flex items-center gap-2">
                    <span>{selectedProfileTenant.companyName || selectedProfileTenant.fullName || selectedProfileTenant.username}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-mono font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                      ID: #{selectedProfileTenant.id}
                    </span>
                  </h3>
                  <div className="text-xs text-text-muted flex items-center gap-2 mt-0.5">
                    <span className="font-mono">@{selectedProfileTenant.username}</span>
                    <span>•</span>
                    <span className="capitalize">{selectedProfileTenant.industry || "general"}</span>
                    <span>•</span>
                    <span>Joined: {new Date(selectedProfileTenant.createdAt).toLocaleDateString("en-IN")}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedProfileTenant(null)}
                className="text-text-muted hover:text-foreground text-sm p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quick Multi-Channel Action Bar */}
            <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-bold text-slate-600 dark:text-slate-400">Quick Communicate:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {/* 1. Direct Call */}
                {selectedProfileTenant.phone ? (
                  <a
                    href={`tel:${selectedProfileTenant.phone.replace(/[^0-9+]/g, "")}`}
                    className="px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
                    title="Call directly from phone or desktop"
                  >
                    <PhoneCall className="w-3.5 h-3.5" />
                    Call {selectedProfileTenant.phone}
                  </a>
                ) : (
                  <span className="text-xs text-text-muted italic">No phone for calls</span>
                )}

                {/* 2. Direct WhatsApp Web */}
                {selectedProfileTenant.phone && (
                  <a
                    href={`https://wa.me/${selectedProfileTenant.phone.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
                    title="Open chat in WhatsApp Web or App"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp Chat
                  </a>
                )}

                {/* 3. Direct Native SMS */}
                {selectedProfileTenant.phone && (
                  <a
                    href={`sms:${selectedProfileTenant.phone.replace(/[^0-9+]/g, "")}`}
                    className="px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
                    title="Send native SMS from device"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    SMS
                  </a>
                )}

                {/* 4. Direct Email */}
                {selectedProfileTenant.email && (
                  <a
                    href={`mailto:${selectedProfileTenant.email}`}
                    className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Email
                  </a>
                )}
              </div>
            </div>

            {/* Navigation Tabs inside Profile Modal */}
            <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
              <button
                onClick={() => setActiveCommTab("overview")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                  activeCommTab === "overview"
                    ? "bg-primary text-white shadow-sm"
                    : "text-text-muted hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                Profile Overview
              </button>
              <button
                onClick={() => setActiveCommTab("trial")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                  activeCommTab === "trial"
                    ? "bg-primary text-white shadow-sm"
                    : "text-text-muted hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                Trial Controls &amp; Expiry
              </button>
              <button
                onClick={() => setActiveCommTab("whatsapp")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                  activeCommTab === "whatsapp"
                    ? "bg-primary text-white shadow-sm"
                    : "text-text-muted hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                WhatsApp Automation
              </button>
              <button
                onClick={() => setActiveCommTab("sms")}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                  activeCommTab === "sms"
                    ? "bg-primary text-white shadow-sm"
                    : "text-text-muted hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                SMS Gateway
              </button>
            </div>

            {/* Tab 1: Profile Overview */}
            {activeCommTab === "overview" && (
              <div className="space-y-4 text-xs">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <span className="text-[10px] text-text-muted font-bold uppercase">Contact Person</span>
                    <div className="font-bold text-foreground text-sm mt-0.5">{selectedProfileTenant.fullName || "—"}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <span className="text-[10px] text-text-muted font-bold uppercase">Email Address</span>
                    <div className="font-semibold text-foreground text-xs mt-0.5 truncate">{selectedProfileTenant.email || "—"}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <span className="text-[10px] text-text-muted font-bold uppercase">Phone Number</span>
                    <div className="font-bold text-foreground text-xs mt-0.5">{selectedProfileTenant.phone || "—"}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <span className="text-[10px] text-text-muted font-bold uppercase">Pipeline Leads</span>
                    <div className="font-black text-foreground text-base mt-0.5">{selectedProfileTenant.leadCount}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <span className="text-[10px] text-text-muted font-bold uppercase">WhatsApp Quota</span>
                    <div className="font-bold text-foreground text-xs mt-0.5">
                      {selectedProfileTenant.whatsappUsed || 0} / {selectedProfileTenant.whatsappLimit || 100}
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <span className="text-[10px] text-text-muted font-bold uppercase">Current Plan</span>
                    <div className="font-black text-primary text-xs uppercase mt-0.5">{selectedProfileTenant.planTier}</div>
                  </div>
                </div>

                {/* Workspace Switcher */}
                {onSwitchWorkspace && (
                  <div className="p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 flex items-center justify-between">
                    <div>
                      <div className="font-bold text-blue-900 dark:text-blue-300">Switch into this Workspace</div>
                      <div className="text-[11px] text-blue-700 dark:text-blue-400">View live Kanban, invoices, and settings as this tenant.</div>
                    </div>
                    <button
                      onClick={() => {
                        onSwitchWorkspace(selectedProfileTenant.id);
                        setSelectedProfileTenant(null);
                      }}
                      className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-1 shadow-sm transition-colors cursor-pointer"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open Workspace
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Tab 2: Trial Controls (Increase / Decrease & Date Picker) */}
            {activeCommTab === "trial" && (
              <div className="space-y-4 text-xs">
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-text-muted font-bold uppercase">Current Trial Expiry</span>
                      <div className="font-black text-foreground text-sm mt-0.5">
                        {selectedProfileTenant.trialEndsAt
                          ? new Date(selectedProfileTenant.trialEndsAt).toLocaleDateString("en-IN", {
                              weekday: "short",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })
                          : "No Expiry (Paid Plan)"}
                      </div>
                    </div>
                    <div>
                      {selectedProfileTenant.trialStatus === "expired" ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200">
                          Expired
                        </span>
                      ) : selectedProfileTenant.daysRemaining !== null && selectedProfileTenant.daysRemaining !== undefined ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200">
                          {selectedProfileTenant.daysRemaining} Days Left
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* Incremental Adjusters */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                      Quick Trial Duration Adjusters:
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => handleAdjustTrial(selectedProfileTenant, -7)}
                        disabled={actionLoadingId === selectedProfileTenant.id}
                        className="px-3 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-950/50 hover:bg-rose-100 text-rose-700 dark:text-rose-300 border border-rose-200 font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <MinusCircle className="w-3.5 h-3.5" />
                        -7 Days
                      </button>
                      <button
                        onClick={() => handleAdjustTrial(selectedProfileTenant, -1)}
                        disabled={actionLoadingId === selectedProfileTenant.id}
                        className="px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-950/50 hover:bg-amber-100 text-amber-700 dark:text-amber-300 border border-amber-200 font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <MinusCircle className="w-3.5 h-3.5" />
                        -1 Day
                      </button>
                      <button
                        onClick={() => handleAdjustTrial(selectedProfileTenant, 1)}
                        disabled={actionLoadingId === selectedProfileTenant.id}
                        className="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 border border-emerald-200 font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <PlusCircle className="w-3.5 h-3.5" />
                        +1 Day
                      </button>
                      <button
                        onClick={() => handleAdjustTrial(selectedProfileTenant, 7)}
                        disabled={actionLoadingId === selectedProfileTenant.id}
                        className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-1 shadow-sm cursor-pointer"
                      >
                        <PlusCircle className="w-3.5 h-3.5" />
                        +7 Days (Extension)
                      </button>
                    </div>
                  </div>

                  {/* Exact Date Picker */}
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                    <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                      Or Set Exact Expiration Date:
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={customExpiryDate}
                        onChange={(e) => setCustomExpiryDate(e.target.value)}
                        className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary shadow-inner"
                      />
                      <Button
                        variant="gradient"
                        size="sm"
                        onClick={handleSetExactDate}
                        disabled={!customExpiryDate || actionLoadingId === selectedProfileTenant.id}
                        className="text-xs font-bold"
                      >
                        Apply Date
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Plan Tier Selection */}
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] text-text-muted font-bold uppercase">Upgrade / Change Plan Tier</span>
                    <div className="text-xs text-text-muted mt-0.5">Switch tenant between Trial or Paid subscription tiers</div>
                  </div>
                  <select
                    value={selectedProfileTenant.planTier || "trial"}
                    onChange={(e) => handleChangePlan(selectedProfileTenant.id, e.target.value)}
                    className="text-xs font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-foreground rounded-xl px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary shadow-sm"
                  >
                    <option value="trial">Trial (Free 7-Day)</option>
                    <option value="starter">Starter (₹1,999/mo)</option>
                    <option value="growth">Growth (₹4,999/mo)</option>
                    <option value="professional">Professional (₹9,999/mo)</option>
                    <option value="enterprise">Enterprise (Custom)</option>
                  </select>
                </div>
              </div>
            )}

            {/* Tab 3: WhatsApp Automation & Alerts */}
            {activeCommTab === "whatsapp" && (
              <div className="space-y-4 text-xs">
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-emerald-600" />
                    <div>
                      <div className="font-bold text-emerald-900 dark:text-emerald-300">Recipient Phone</div>
                      <div className="text-[11px] text-emerald-700 dark:text-emerald-400">{selectedProfileTenant.phone || "No phone registered"}</div>
                    </div>
                  </div>
                  {selectedProfileTenant.phone && (
                    <a
                      href={`https://wa.me/${selectedProfileTenant.phone.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs"
                    >
                      Open WhatsApp Web
                    </a>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    Dispatch Automated WhatsApp Alert Template:
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 hover:border-primary/50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="notifType"
                        checked={notificationType === "expiring_soon"}
                        onChange={() => setNotificationType("expiring_soon")}
                        className="mt-0.5 text-primary focus:ring-primary"
                      />
                      <div>
                        <div className="text-xs font-bold text-foreground">⏳ Trial Ending Soon Reminder</div>
                        <div className="text-[11px] text-text-muted mt-0.5">
                          Reminds client that their trial ends in a few days and prompts them to upgrade to keep pipelines active.
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 hover:border-primary/50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="notifType"
                        checked={notificationType === "expired"}
                        onChange={() => setNotificationType("expired")}
                        className="mt-0.5 text-rose-600 focus:ring-rose-500"
                      />
                      <div>
                        <div className="text-xs font-bold text-rose-600 dark:text-rose-400">🛑 Urgent: Trial Expired &amp; Data Lock Warning</div>
                        <div className="text-[11px] text-text-muted mt-0.5">
                          Warns client that their free trial has expired and prompts urgent payment before workflows lock.
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 hover:border-primary/50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="notifType"
                        checked={notificationType === "extension"}
                        onChange={() => setNotificationType("extension")}
                        className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div>
                        <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400">🎉 Trial Extension Granted</div>
                        <div className="text-[11px] text-text-muted mt-0.5">
                          Congratulates tenant on trial extension and provides link to their live CRM.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="gradient"
                    size="sm"
                    onClick={handleSendWhatsAppNotification}
                    disabled={!selectedProfileTenant.phone || isSendingWhatsApp}
                    className="text-xs font-bold flex items-center gap-1.5"
                  >
                    {isSendingWhatsApp ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        <span>Sending WhatsApp...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Dispatch WhatsApp Alert</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Tab 4: Direct SMS Gateway */}
            {activeCommTab === "sms" && (
              <div className="space-y-4 text-xs">
                <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/60 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-sky-600" />
                    <div>
                      <div className="font-bold text-sky-900 dark:text-sky-300">Send SMS to {selectedProfileTenant.phone || "No phone"}</div>
                      <div className="text-[11px] text-sky-700 dark:text-sky-400">Delivered via Twilio / SMS Gateway</div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    Compose Custom SMS Message:
                  </label>
                  <textarea
                    rows={4}
                    value={customSmsText}
                    onChange={(e) => setCustomSmsText(e.target.value)}
                    placeholder="Type urgent SMS notification or upgrade offer for this tenant..."
                    className="w-full p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-foreground placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary shadow-inner resize-none"
                  />
                  <div className="text-[10px] text-text-muted text-right mt-1">
                    {customSmsText.length} characters (1 SMS segment = 160 chars)
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="gradient"
                    size="sm"
                    onClick={handleSendCustomSms}
                    disabled={!selectedProfileTenant.phone || !customSmsText.trim() || isSendingSms}
                    className="text-xs font-bold flex items-center gap-1.5"
                  >
                    {isSendingSms ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        <span>Dispatching SMS...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Send SMS Message</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Modal Footer & Danger Zone */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setTenantToDelete(selectedProfileTenant)}
                className="text-xs font-bold text-rose-600 hover:text-rose-700 flex items-center gap-1 hover:underline cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Tenant Account
              </button>

              <button
                type="button"
                onClick={() => setSelectedProfileTenant(null)}
                className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-text-muted hover:text-foreground transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Tenant Confirmation Dialog */}
      {tenantToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-md glass-card-premium rounded-2xl p-6 shadow-2xl border border-rose-200 dark:border-rose-900 bg-white dark:bg-slate-900 text-foreground space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="p-3 rounded-2xl bg-rose-100 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-800">
                <Trash2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-heading font-black text-foreground text-base">Permanently Delete Tenant?</h3>
                <p className="text-xs text-rose-600 dark:text-rose-400 font-semibold">This action cannot be undone.</p>
              </div>
            </div>

            <p className="text-xs text-text-muted leading-relaxed">
              Are you sure you want to delete <strong className="text-foreground">{tenantToDelete.companyName || tenantToDelete.username}</strong> (ID: #{tenantToDelete.id})? All {tenantToDelete.leadCount} leads, invoices, subscriptions, and configurations associated with this tenant will be permanently wiped.
            </p>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setTenantToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-text-muted hover:text-foreground transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteTenant}
                disabled={isDeleting}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Yes, Permanently Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
